import { useEffect, useMemo, useState } from 'react';
import { groupColors, getGroupColorByName } from '../groupColors';
import { useLockBodyScroll } from './useLockBodyScroll';

function formatPhoneDisplay(phone) {
  if (!phone) return '';
  if (phone.startsWith('+1')) {
    const digits = phone.slice(2);
    return `+1 ${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (phone.startsWith('+33')) {
    const digits = phone.slice(3);
    return `+33 ${digits.slice(0, 1)} ${digits.slice(1, 3)} ${digits.slice(3, 5)} ${digits.slice(5, 7)} ${digits.slice(7)}`;
  }
  return phone;
}

export default function UpdateContactModal({
  isOpen,
  contact,
  groups,
  trads,
  onClose,
  onUpdated,
}) {
  const t = trads?.contacts || {};
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [newGroupName, setNewGroupName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useLockBodyScroll(isOpen);

  useEffect(() => {
    if (isOpen && contact) {
      const groupIds = (contact.groupMemberships || []).map(
        (group) => group.id
      );
      setSelectedGroupIds(groupIds);
      setNewGroupName('');
      setError('');
    }
  }, [isOpen, contact]);

  const selectedGroups = useMemo(() => {
    const selectedIds = new Set(selectedGroupIds);
    return (groups || []).filter((group) => selectedIds.has(group.id));
  }, [groups, selectedGroupIds]);

  const availableGroups = useMemo(() => {
    const selectedIds = new Set(selectedGroupIds);
    return (groups || []).filter((group) => !selectedIds.has(group.id));
  }, [groups, selectedGroupIds]);

  if (!isOpen || !contact) {
    return null;
  }

  const updateGroups = async ({ groupIds, newGroup }) => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/contact/update-groups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contactId: contact.id,
          groupIds,
          ...(newGroup ? { newGroup } : {}),
        }),
      });

      const data = await response.json();
      if (!data.success) {
        setError(
          data.error || t.failedToUpdateGroups || 'Failed to update groups'
        );
        return;
      }

      const nextIds = (data.contact?.groupMemberships || []).map(
        (group) => group.id
      );
      setSelectedGroupIds(nextIds);
      setNewGroupName('');
      onUpdated?.(data.contact, data.groups);
    } catch (err) {
      console.error('Failed to update contact groups:', err);
      setError(t.failedToUpdateGroups || 'Failed to update groups');
    } finally {
      setLoading(false);
    }
  };

  const removeGroup = async (groupId) => {
    const nextGroupIds = selectedGroupIds.filter((id) => id !== groupId);
    await updateGroups({ groupIds: nextGroupIds });
  };

  const addGroup = async (groupId) => {
    const nextGroupIds = [...new Set([...selectedGroupIds, groupId])];
    await updateGroups({ groupIds: nextGroupIds });
  };

  const createGroupAndAdd = async () => {
    const trimmedName = newGroupName.trim();
    if (!trimmedName) {
      return;
    }

    const color = groupColors[groups.length % groupColors.length].name;
    await updateGroups({
      groupIds: selectedGroupIds,
      newGroup: {
        name: trimmedName,
        color,
      },
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto"
        data-testid="update-contact-modal"
      >
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-xl font-bold text-gray-900">
            {t.updateContactTitle || 'Update contact'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="update-contact-close-btn"
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t.phoneNumber || 'Phone number'}
            </label>
            <input
              type="text"
              disabled
              data-testid="update-contact-phone-input"
              value={formatPhoneDisplay(contact.phone)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-700"
            />
          </div>

          {/* commented out, now passive opt-in system for US */}
          {contact.countryCode === 'CA' &&
            !contact.optedInAt &&
            contact.mostRecentOptInMessage && (
              <div className="text-sm bg-amber-100 text-yellow-700 p-2 rounded-md border border-yellow-400">
                ⚠️{' '}
                {(
                  t.optInWarningUS ||
                  'Contact must reply "yes" to the SMS received from {phoneNumber}'
                ).replace(
                  '{phoneNumber}',
                  formatPhoneDisplay(
                    contact.mostRecentOptInMessage.senderPhone?.phoneNumber ||
                      ''
                  )
                )}
              </div>
            )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t.groupsLabel || 'Groups'}
            </label>
            <p className="font-bold text-sm text-gray-900 mb-2">
              {t.contactBelongsGroups ||
                'This contact belongs to the following groups :'}
            </p>
            {selectedGroups.length === 0 ? (
              <p className="text-sm text-gray-500">
                {t.contactNoGroups || 'No groups yet'}
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {selectedGroups.map((group) => {
                  const color = getGroupColorByName(group.color);
                  return (
                    <div
                      key={group.id}
                      data-testid={`contact-group-badge-${group.id}`}
                      className="px-3 py-2 rounded-md flex items-center justify-between text-sm"
                      style={{
                        backgroundColor: color.background,
                        color: color.text,
                      }}
                    >
                      <span className="font-medium truncate mr-2">
                        {group.name}
                      </span>
                      <button
                        type="button"
                        data-testid={`remove-contact-group-btn-${group.id}`}
                        className="ml-2 inline-flex h-6 w-6 items-center justify-center rounded-full text-lg font-bold leading-none cursor-pointer hover:opacity-80 disabled:cursor-not-allowed"
                        onClick={() => removeGroup(group.id)}
                        disabled={loading}
                        aria-label={`Remove ${group.name} group`}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <hr className="border-gray-200" />

          <div>
            <p className="font-bold text-sm text-gray-900 mb-2">
              {t.clickGroupToAdd ||
                'Click on an existing group to add the contact to it'}
            </p>
            {availableGroups.length === 0 ? (
              <p className="text-sm text-gray-500">
                {t.noMoreGroupsToAdd || 'No more groups to add'}
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {availableGroups.map((group) => {
                  const color = getGroupColorByName(group.color);
                  return (
                    <button
                      key={group.id}
                      type="button"
                      onClick={() => addGroup(group.id)}
                      data-testid={`add-contact-to-group-btn-${group.id}`}
                      className="px-2 py-1 rounded text-xs font-medium"
                      style={{
                        backgroundColor: color.background,
                        color: color.text,
                      }}
                      disabled={loading}
                    >
                      {group.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t.addToGroupLabel || 'Add to group'}
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={newGroupName}
                onChange={(event) => setNewGroupName(event.target.value)}
                placeholder={t.newGroupNamePlaceholder || 'New group name'}
                data-testid="create-contact-group-input"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
              <button
                type="button"
                onClick={createGroupAndAdd}
                data-testid="create-contact-group-btn"
                disabled={loading || newGroupName.trim().length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t.createGroupAndAdd || 'Create group and add contact'}
              </button>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
